import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { formatCurrency } from "@/lib/crypto-data";
import { useCommissionRate } from "@/hooks/useCommissionRate";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DollarSign, TrendingUp, Users, ShoppingCart, MessageSquare, Smartphone, Wallet, Percent } from "lucide-react";

interface Transaction {
  total_usd: number;
  network_fee: number;
  user_id: string;
  amount: number;
}

interface WalletBalance {
  crypto_symbol: string;
  balance: number;
}

const AdminStats = () => {
  const { calculateNetProfit } = useCommissionRate();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [chatCount, setChatCount] = useState(0);
  const [deviceCount, setDeviceCount] = useState(0);
  const [blockedDeviceCount, setBlockedDeviceCount] = useState(0);
  const [totalUserBalances, setTotalUserBalances] = useState<WalletBalance[]>([]);

  useEffect(() => {
    const fetchStats = async () => {
      // Fetch transactions
      const { data: txData } = await supabase
        .from("transactions")
        .select("total_usd, network_fee, user_id, amount");
      
      if (txData) setTransactions(txData as Transaction[]);

      // Fetch chat messages count
      const { count: chatMsgCount } = await supabase
        .from("chat_messages")
        .select("*", { count: "exact", head: true })
        .eq("role", "user");
      
      setChatCount(chatMsgCount || 0);

      // Fetch devices count
      const { count: devCount } = await supabase
        .from("user_devices")
        .select("*", { count: "exact", head: true });
      
      setDeviceCount(devCount || 0);

      // Fetch blocked devices count
      const { count: blockedCount } = await supabase
        .from("user_devices")
        .select("*", { count: "exact", head: true })
        .eq("is_blocked", true);
      
      setBlockedDeviceCount(blockedCount || 0);

      // Fetch all user wallet balances
      const { data: walletsData } = await supabase
        .from("wallets")
        .select("crypto_symbol, balance");
      
      if (walletsData) {
        // Aggregate balances by crypto_symbol
        const aggregated: Record<string, number> = {};
        (walletsData as WalletBalance[]).forEach((w) => {
          aggregated[w.crypto_symbol] = (aggregated[w.crypto_symbol] || 0) + Number(w.balance);
        });
        const balances = Object.entries(aggregated)
          .map(([crypto_symbol, balance]) => ({ crypto_symbol, balance }))
          .filter((b) => b.balance > 0)
          .sort((a, b) => b.balance - a.balance);
        setTotalUserBalances(balances);
      }
    };

    fetchStats();
  }, []);

  const totalRevenue = transactions.reduce((sum, t) => sum + t.total_usd, 0);
  const totalProfit = transactions.reduce((sum, t) => {
    return sum + calculateNetProfit(t.total_usd, t.amount);
  }, 0);
  const profitPercentage = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;
  const totalTransactions = transactions.length;
  const uniqueUsers = new Set(transactions.map(t => t.user_id)).size;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7 gap-4">
      <Card className="glass border-border">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            إجمالي الإيرادات
          </CardTitle>
          <DollarSign className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-foreground">
            {formatCurrency(totalRevenue)}
          </div>
        </CardContent>
      </Card>

      <Card className="glass border-border">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            إجمالي الأرباح
          </CardTitle>
          <TrendingUp className="h-4 w-4 text-crypto-green" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-crypto-green">
            {formatCurrency(totalProfit)}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            حسب شرائح العمولة
          </p>
        </CardContent>
      </Card>

      <Card className="glass border-border">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            نسبة الأرباح
          </CardTitle>
          <Percent className="h-4 w-4 text-primary" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-primary">
            {profitPercentage.toFixed(1)}%
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            من إجمالي الإيرادات
          </p>
        </CardContent>
      </Card>
      <Card className="glass border-border">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            عدد المعاملات
          </CardTitle>
          <ShoppingCart className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-foreground">
            {totalTransactions}
          </div>
        </CardContent>
      </Card>

      <Card className="glass border-border">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            عدد المستخدمين
          </CardTitle>
          <Users className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-foreground">
            {uniqueUsers}
          </div>
        </CardContent>
      </Card>

      <Card className="glass border-border">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            رسائل الدعم
          </CardTitle>
          <MessageSquare className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-foreground">
            {chatCount}
          </div>
        </CardContent>
      </Card>

      <Card className="glass border-border">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            الأجهزة
          </CardTitle>
          <Smartphone className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-foreground">
            {deviceCount}
          </div>
          {blockedDeviceCount > 0 && (
            <p className="text-xs text-destructive mt-1">
              {blockedDeviceCount} محظور
            </p>
          )}
        </CardContent>
      </Card>

      <Card className="glass border-border col-span-full">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            إجمالي أرصدة المستخدمين
          </CardTitle>
          <Wallet className="h-4 w-4 text-primary" />
        </CardHeader>
        <CardContent>
          {totalUserBalances.length > 0 ? (
            <div className="flex flex-wrap gap-4">
              {totalUserBalances.map((b) => (
                <div key={b.crypto_symbol} className="flex items-center gap-2 bg-secondary/50 rounded-lg px-3 py-2">
                  <span className="font-bold text-foreground">{b.crypto_symbol}</span>
                  <span className="text-primary font-mono">{b.balance.toLocaleString(undefined, { maximumFractionDigits: 8 })}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">لا توجد أرصدة حالياً</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminStats;
